import { fromEntries } from "./browserCompat";
const clone = (value) => {
  if (value == null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(clone);
  return fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]));
};

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const objectValue = (value) => (isPlainObject(value) ? value : {});

function mergeDeep(base, source) {
  const output = clone(base);
  Object.entries(objectValue(source)).forEach(([key, value]) => {
    output[key] = isPlainObject(value) && isPlainObject(output[key])
      ? mergeDeep(output[key], value)
      : clone(value);
  });
  return output;
}

function hexToRgba(value, alpha, fallback) {
  const candidate = String(value || "").trim();
  const match = candidate.match(/^#([0-9a-fA-F]{6})$/);
  if (!match) return fallback;
  const raw = match[1];
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export const WEB_PUSH_PROMPT_STYLE_DEFAULTS = Object.freeze({
  surface: Object.freeze({
    backgroundColor: "#FFFFFF",
    borderColor: "rgba(25,36,47,.12)",
    borderWidth: 1,
    borderRadius: 30,
    boxShadow: "0 12px 30px rgba(25,36,47,.18)",
  }),
  icon: Object.freeze({
    color: "#F35A3A",
    surfaceBackgroundColor: "rgba(243,90,58,.10)",
    borderColor: "transparent",
    borderWidth: 0,
    borderRadius: 20,
  }),
  title: Object.freeze({ color: "#1A1A2E" }),
  subscribedTitle: Object.freeze({ color: "#1A1A2E" }),
  description: Object.freeze({ color: "#5B5D73" }),
  subscribedDescription: Object.freeze({ color: "#5B5D73" }),
  enableButton: Object.freeze({
    backgroundColor: "#F35A3A",
    textColor: "#FFFFFF",
    borderColor: "#F35A3A",
    borderWidth: 1,
    hoverBackgroundColor: "#F35A3A",
    hoverTextColor: "#FFFFFF",
    hoverBorderColor: "#F35A3A",
    focusRingColor: "rgba(243,90,58,.35)",
    borderRadius: 10,
    boxShadow: "none",
  }),
  disableButton: Object.freeze({
    backgroundColor: "transparent",
    textColor: "#F35A3A",
    borderColor: "rgba(243,90,58,.50)",
    borderWidth: 1,
    hoverBackgroundColor: "rgba(243,90,58,.08)",
    hoverTextColor: "#F35A3A",
    hoverBorderColor: "#F35A3A",
    focusRingColor: "rgba(243,90,58,.35)",
    borderRadius: 10,
    boxShadow: "none",
  }),
  dismissButton: Object.freeze({
    backgroundColor: "transparent",
    textColor: "#1A1A2E",
    borderColor: "transparent",
    borderWidth: 1,
    hoverBackgroundColor: "rgba(25,36,47,.06)",
    hoverTextColor: "#1A1A2E",
    hoverBorderColor: "transparent",
    focusRingColor: "rgba(25,36,47,.22)",
    borderRadius: 10,
    boxShadow: "none",
  }),
  processing: Object.freeze({
    backgroundColor: "rgba(243,90,58,.55)",
    textColor: "#FFFFFF",
    borderColor: "rgba(243,90,58,.55)",
  }),
  success: Object.freeze({
    backgroundColor: "#EDF7ED",
    textColor: "#1E4620",
    borderColor: "#A5D6A7",
    borderWidth: 1,
    iconColor: "#2E7D32",
    borderRadius: 10,
    boxShadow: "0 8px 20px rgba(25,36,47,.14)",
  }),
  error: Object.freeze({
    backgroundColor: "#FDEDED",
    textColor: "#5F2120",
    borderColor: "#F5C2C0",
    borderWidth: 1,
    iconColor: "#D32F2F",
    borderRadius: 10,
  }),
});

export function legacyWebPushPromptStyle(prompt = {}, theme = {}) {
  const primary = String(theme?.primaryColor || "#F35A3A").trim() || "#F35A3A";
  const mainText = String(prompt?.textColor || theme?.textColor || "#1A1A2E").trim() || "#1A1A2E";
  const mutedText = String(prompt?.mutedTextColor || theme?.mutedTextColor || "#5B5D73").trim() || "#5B5D73";
  const surface = String(prompt?.backgroundColor || theme?.surfaceColor || "#FFFFFF").trim() || "#FFFFFF";
  const divider = String(theme?.dividerColor || "rgba(25,36,47,.12)").trim() || "rgba(25,36,47,.12)";
  const radiusBase = Number(theme?.borderRadius);
  const surfaceRadius = Number.isFinite(radiusBase) ? Math.max(0, Math.min(160, radiusBase * 3)) : 30;
  const primarySoft = hexToRgba(primary, 0.08, "rgba(243,90,58,.08)");
  const primaryMedium = hexToRgba(primary, 0.5, "rgba(243,90,58,.50)");
  const primaryBusy = hexToRgba(primary, 0.55, "rgba(243,90,58,.55)");
  const primaryFocus = hexToRgba(primary, 0.35, "rgba(243,90,58,.35)");

  return mergeDeep(WEB_PUSH_PROMPT_STYLE_DEFAULTS, {
    surface: { backgroundColor: surface, borderColor: divider, borderRadius: surfaceRadius },
    icon: { color: primary, surfaceBackgroundColor: primarySoft },
    title: { color: mainText },
    subscribedTitle: { color: mainText },
    description: { color: mutedText },
    subscribedDescription: { color: mutedText },
    enableButton: {
      backgroundColor: primary,
      borderColor: primary,
      hoverBackgroundColor: primary,
      hoverBorderColor: primary,
      focusRingColor: primaryFocus,
    },
    disableButton: {
      textColor: primary,
      borderColor: primaryMedium,
      hoverBackgroundColor: primarySoft,
      hoverTextColor: primary,
      hoverBorderColor: primary,
      focusRingColor: primaryFocus,
    },
    dismissButton: {
      textColor: mainText,
      hoverTextColor: mainText,
      focusRingColor: hexToRgba(mainText, 0.22, "rgba(25,36,47,.22)"),
    },
    processing: {
      backgroundColor: primaryBusy,
      borderColor: primaryBusy,
    },
  });
}

export function materializeWebPushPromptVisualOwnership(content = {}) {
  const source = clone(content);
  const notifications = objectValue(source.notifications);
  const prompt = objectValue(notifications.webPushPrompt);
  const legacy = legacyWebPushPromptStyle(prompt, source.theme);
  const style = mergeDeep(legacy, prompt.style);
  delete prompt.backgroundColor;
  delete prompt.textColor;
  delete prompt.mutedTextColor;
  source.notifications = {
    ...notifications,
    webPushPrompt: { ...prompt, style },
  };
  return source;
}
